import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Mail, ArrowDown } from "lucide-react";
import { CONTACT, HERO_IMAGE } from "@/lib/siteData";

const lineVariants = {
  hidden: { y: "115%", opacity: 0, rotate: 2 },
  visible: (i) => ({
    y: "0%",
    opacity: 1,
    rotate: 0,
    transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.18 },
  }),
};

export const KineticHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen pt-32 lg:pt-40 pb-16 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#9E5A38] mb-8"
            data-testid="hero-eyebrow"
          >
            Artisan Peintre & Rénovateur du Bâtiment
          </motion.p>

          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-[4.5rem] font-light tracking-tight leading-[1.05] text-[#1C1815]">
            <span className="block overflow-hidden pb-1">
              <motion.span
                custom={0}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block will-change-transform"
                data-testid="hero-title-line-1"
              >
                L'art de la matière,
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                custom={1}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block will-change-transform italic text-[#9E5A38]"
                data-testid="hero-title-line-2"
              >
                l'exigence du geste.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-8 text-lg sm:text-xl font-light leading-relaxed text-[#59524A] max-w-2xl"
            data-testid="hero-subtitle"
          >
            Un professionnel à votre service et à votre écoute tout au long de
            votre projet, qui respectera vos envies et vos goûts. Un
            accompagnement complet lors de votre processus de décision et
            durant le chantier. Une orientation vers des solutions pratiques et
            techniques à la hauteur de vos exigences.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={CONTACT.phoneHref}
              data-testid="hero-call-button"
              className="inline-flex items-center gap-2.5 rounded-full bg-[#1A1714] text-[#FAF7F2] px-7 py-3.5 text-sm font-medium hover:bg-[#9E5A38] transition-colors duration-300"
            >
              <Phone className="w-4 h-4" />
              Appeler Louis Larsson
            </a>
            <a
              href={CONTACT.emailHref}
              data-testid="hero-email-button"
              className="inline-flex items-center gap-2.5 rounded-full border border-[#1A1714]/20 text-[#1A1714] px-7 py-3.5 text-sm font-medium hover:border-[#9E5A38] hover:text-[#9E5A38] transition-colors duration-300"
            >
              <Mail className="w-4 h-4" />
              Écrire un e-mail
            </a>
            <a
              href="#galerie"
              data-testid="hero-explore-button"
              className="group inline-flex items-center gap-2 text-sm font-medium text-[#574E45] hover:text-[#1A1714] transition-colors duration-300"
            >
              Explorer les réalisations
              <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" />
            </a>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-sm border border-[#1A1714]/10 shadow-[0_40px_80px_-30px_rgba(26,23,20,0.35)]">
              <motion.img
                style={{ y: imageY, scale: 1.15 }}
                src={HERO_IMAGE}
                alt="Louis Larsson, artisan peintre, appliquant la peinture au rouleau en tenue de travail impeccable"
                className="w-full h-[420px] lg:h-[560px] object-cover will-change-transform"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1714]/25 via-transparent to-transparent" />
            </div>

            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 1.3 }}
              className="absolute -left-4 lg:-left-10 top-8 bg-[#FAF7F2]/90 backdrop-blur-md border border-[#1A1714]/10 rounded-full px-5 py-3 shadow-lg"
              data-testid="hero-badge-experience"
            >
              <span className="font-serif-display italic text-sm text-[#1A1714]">
                Artisan Qualifié — <span className="font-mono-tech not-italic text-xs">Depuis 2012</span>
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 1.5 }}
              className="absolute -right-2 lg:-right-6 bottom-10 bg-[#1A1714] text-[#FAF7F2] rounded-sm px-5 py-4 shadow-xl"
              data-testid="hero-badge-availability"
            >
              <span className="flex items-center gap-2.5 text-xs font-medium">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#7FB285] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#7FB285]" />
                </span>
                Disponible pour vos chantiers ce trimestre
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
